<template>
  <div class="container">
    <div v-if="item.user" class="author-container">
      <img
        v-if="item.user.photoURL"
        :src="item.user.photoURL"
        class="author-image"
        alt=""
      />
      <img
        v-else
        class="author-image"
        :src="require('@/assets/default-avatar.png')"
      />
      <span class="author-name">{{ getUserName(item.user) }}</span>
      <img
        :src="openIcon"
        alt=""
        class="open-chart-button"
        @click="goToUserDetails"
      />
    </div>
    <div style="display: flex; align-items: center; gap: 16px;">
      <div class="workout-name">{{ getWorkoutName(item) }} - {{ getWorkoutType(item) }}</div>
      <img
        v-if="item.restype !== 'freestyle' && item.restype !== 'byo'"
        :src="openIcon"
        alt=""
        class="open-chart-button"
        @click="goToWorkoutDetails"
      />
    </div>
    <div class="result-date">{{ getWorkoutDate(item) }}</div>

    <div class="segmented-menu">
      <button
        v-for="option in menuOptions"
        :key="option"
        :class="['menu-option', { active: selectedOption === option }]"
        @click="selectedOption = option"
      >
        {{ option }}
      </button>
    </div>

    <result-details v-if="selectedOption === 'Details'" :item="item" />
    <result-vbt v-if="selectedOption === 'VBT'" :item="item" />

    <div v-if="shouldShowExercisesList(item) && selectedOption === 'Details'" class="exercises-list">
      <div class="workout-name" style="margin-top: 16px; margin-bottom: 16px">
        Exercises
      </div>
      <div class="exercises-container">
        <div v-for="(exercise, index) in getExercisesList(item)" :key="index" class="exercise-item">
          <div class="exercise-image-container">
            <img 
              :src="require(`@/assets/maxpro-X.jpg`)" 
              alt="Exercise"
              class="exercise-image"
            />
          </div>
          <div class="exercise-data">
            <div class="exercise-name">{{ exercise.name }}</div>
            <div class="exercise-metrics">
              <div>
                <span>Calories:</span> <span class="orange-value">{{ exercise.calories?.toFixed(1) }}</span>
              </div>
              <div class="knob-container">
                <div class="knob-item">
                  <span class="knob-label">Knob Left:</span>
                  <span class="orange-value">{{ exercise.previousL || 0 }}</span>
                </div>
                <div class="knob-item">
                  <span class="knob-label">Knob Right:</span>
                  <span class="orange-value">{{ exercise.previousR || 0 }}</span>
                </div>
              </div>
              <div class="reps-container">
                <div class="reps-item">
                  <span class="reps-label">Reps Left:</span>
                  <span class="orange-value">{{ exercise.repsL || 0 }}</span>
                </div>
                <div class="reps-item">
                  <span class="reps-label">Reps Right:</span>
                  <span class="orange-value">{{ exercise.repsR || 0 }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="exercise-mxp">
            <div class="mxp-display">
              <img :src="require('@/assets/mxp_icon.svg')" alt="MXP" class="mxp-icon" />
              <span class="mxp-value">{{ exercise.mxp?.toFixed(1) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ResultDetails from './ResultDetails.vue';
import ResultVBT from './ResultVBT.vue';
import { getKnobFromKg } from './knobResistance';

export default {
  name: "result-detail",
  components: {
    'result-details': ResultDetails,
    'result-vbt': ResultVBT
  },
  model: {},
  props: {
    item: {
      type: Object,
      required: true,
      default: undefined,
    },
  },
  data() {
    return {
      menuOptions: ["Details", "HRZ", "VBT"],
      selectedOption: "Details",
      openIcon: require("@/assets/open.svg"),
    };
  },
  methods: {
    getUserName(user) {
      if (user.nickname && user.nickname !== "") {
        return user.nickname;
      }
      if (user.displayName && user.displayName !== "") {
        return user.displayName;
      }

      return `${user.firstName} ${user.lastName}`;
    },
    getWorkoutName(item) {
      switch (item.restype) {
        case "byo":
          return item.session.name;
        case "live":
          return item.session.title.ENG;
        case "preSet":
          return item.session.name.ENG;
        case "freestyle":
          return item.name;
        case "coachedClass":
          return item.session?.name;
        default:
          return "";
      }
    },
    getWorkoutDate(item) {
      return item.createdAt.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    shouldShowExercisesList(item) {
      if (!item) return false;
      if (item.restype === 'coachedClass' || item.restype === 'preSet' || item.restype === 'byo') {
        return true;
      }
      // FreeStyle: la app deriva "ejercicios" agrupando reps del historical
      return item.restype === 'freestyle' && Array.isArray(item.historical) && item.historical.length > 0;
    },
    /**
     * FreeStyle no guarda exercisesSaves: igual que la app, cada rep del
     * historical se convierte a knob (por peso) y las reps consecutivas con
     * el mismo knob forman un "Exercise N".
     */
    getFreestyleExercisesList(item) {
      const withKnob = item.historical.map((rep) => ({
        ...rep,
        knobNumber: getKnobFromKg(rep?.weight || 0, item.deviceVersion),
      }));

      const groups = [];
      withKnob.forEach((rep, index) => {
        if (index === 0 || rep.knobNumber !== withKnob[index - 1].knobNumber) {
          groups.push([rep]);
        } else {
          groups[groups.length - 1].push(rep);
        }
      });

      return groups.map((group, index) => {
        const totals = group.reduce(
          (acc, rep) => {
            acc.calories += rep.cals || 0;
            acc.mxp += rep.mxp || 0;
            if (rep.side === 'L') acc.repsL += 1;
            else if (rep.side === 'R') acc.repsR += 1;
            return acc;
          },
          { calories: 0, mxp: 0, repsL: 0, repsR: 0 }
        );
        const knob = group[0].knobNumber || 0;
        return {
          name: `Exercise ${index + 1}`,
          calories: totals.calories,
          mxp: totals.mxp,
          repsL: totals.repsL,
          repsR: totals.repsR,
          previousL: knob,
          previousR: knob,
        };
      });
    },
    getExercisesList(item) {
      if (!item) return [];

      if (item.restype === 'freestyle' && Array.isArray(item.historical) && item.historical.length > 0) {
        return this.getFreestyleExercisesList(item);
      }

      if (item.restype === 'coachedClass' && item.exercisesProgress) {
        return item.exercisesProgress.map((exercise, index) => {
          const lastKnobHistory = exercise.knobsHistoryExercise?.[exercise.knobsHistoryExercise.length - 1];
          return {
            name: exercise.exerciseSection?.exercise?.title || `Exercise ${index + 1}`,
            calories: exercise.calories || 0,
            mxp: exercise.mxp || 0,
            repsL: exercise.reps?.L || 0,
            repsR: exercise.reps?.R || 0,
            previousL: lastKnobHistory?.knobL || 0,
            previousR: lastKnobHistory?.knobR || 0
          };
        });
      } else if ((item.restype === 'preSet' || item.restype === 'byo') && item.exercisesSaves) {
        return item.exercisesSaves.map((exercise, index) => ({
          name: exercise.exercise?.name?.ENG || `Exercise ${index + 1}`,
          calories: exercise.calsBurnedExercise || 0,
          mxp: exercise.mxp || (exercise.powerMadedExercise / 1000) || 0,
          repsL: exercise.repsL || 0,
          repsR: exercise.repsR || 0,
          previousL: exercise.previousL || 0,
          previousR: exercise.previousR || 0
        }));
      }
      
      return [];
    },
    getWorkoutType(item) {
      switch (item.restype) {
        case "byo":
          return "BYO";
        case "live":
          return "Live";
        case "preSet":
          return "Pre-Set";
        case "freestyle":
          return "Freestyle";
        case "coachedClass":
          return "Coached Class";
        default:
          return "";
      }
    },
    goToUserDetails() {
      this.$router.push({ name: "User Profile", params: this.item.user });
    },
    goToWorkoutDetails() {
      switch (this.item.restype) {
        case "live":
          this.$router.push({ name: "Lives Edit", params: this.item.session });
          break;
        case "preSet":
          this.$router.push({ name: "Pre Sets Edit", params: this.item.session });
          break;
        case "coachedClass":
          this.$router.push({ name: "CoachedClasses Edit Set", params: this.item.session });
          break;
      }
    },
  },
};
</script>

<style scoped>
.author-container {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}
.author-image {
  height: 40px;
  width: 40px;
  border-radius: 20px;
  object-fit: cover;
}
.author-name {
  font-size: 16px;
  font-weight: bold;
  color: #3a3a3a;
}
.workout-name {
  font-size: 16px;
  font-weight: bold;
  color: #3a3a3a;
}
.result-date {
  font-size: 14px;
  color: #3a3a3a;
}
.segmented-menu {
  display: flex;
  background-color: white;
  border-radius: 8px;
  padding: 2px;
  margin: 16px 0 16px 0;
  border-width: 1px;
  border-color: #e4e4e4;
  border-style: solid;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.menu-option {
  flex: 1;
  padding: 4px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #3a3a3a;
  transition: all 0.2s ease;
}

.menu-option.active {
  background-color: #ed702d;
  color: white;
  border-radius: 4px;
}

.exercises-list {
  margin-top: 24px;
  background-color: white;
  border-radius: 8px;
}

.exercises-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.exercise-item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 16px;
  padding: 16px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background-color: #f9f9f9;
}

.exercise-image-container {
  display: flex;
  align-items: center;
}

.exercise-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
}

.exercise-data {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.exercise-name {
  font-size: 16px;
  font-weight: bold;
  color: #3a3a3a;
}

.exercise-metrics {
  display: flex;
  flex-direction: column;
}

.metric {
  display: flex;
  align-items: center;
  gap: 8px;
}

.metric-icon {
  width: 16px;
  height: 16px;
}

.metric span {
  font-size: 14px;
  color: #3a3a3a;
}

.reps-container {
  display: flex;
  gap: 16px;
}

.reps-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.reps-label {
  font-size: 14px;
  color: #3a3a3a;
}

.reps-value {
  font-size: 14px;
  color: #ed702d;
  font-weight: bold;
}

.exercise-mxp {
  display: flex;
  align-items: center;
  justify-content: center;
}

.mxp-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mxp-icon {
  width: 40px;
  height: 24px;
}

.mxp-value {
  font-size: 16px;
  font-weight: bold;
  color: #ed702d;
}

.knob-container {
  display: flex;
  gap: 16px;
}

.knob-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.knob-label {
  font-size: 14px;
  color: #3a3a3a;
}

.knob-value {
  font-size: 14px;
  color: #ed702d;
  font-weight: bold;
}

.orange-value {
  font-size: 14px;
  color: #ed702d;
  font-weight: bold;
}
.open-chart-button {
  height: 16px;
  width: 16px;
  transition: all 0.2s ease-in-out;
  opacity: 0.5;
}
.open-chart-button:hover {
  cursor: pointer;
  transform: scale(1.1);
  opacity: 1;
}
</style>
